import React, { useState, useRef } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';


const ContentEditor = ({ 
  language, 
  content, 
  onContentChange, 
  onImageInsert,
  isRTL = false 
}) => {
  const [showToolbar, setShowToolbar] = useState(true);
  const [wordCount, setWordCount] = useState(0);
  const editorRef = useRef(null);

  const toolbarButtons = [
    { name: 'Bold', icon: 'Bold', command: 'bold' },
    { name: 'Italic', icon: 'Italic', command: 'italic' },
    { name: 'Underline', icon: 'Underline', command: 'underline' },
    { name: 'Separator', type: 'separator' },
    { name: 'Heading 1', icon: 'Heading1', command: 'formatBlock', value: 'h1' },
    { name: 'Heading 2', icon: 'Heading2', command: 'formatBlock', value: 'h2' },
    { name: 'Heading 3', icon: 'Heading3', command: 'formatBlock', value: 'h3' },
    { name: 'Separator', type: 'separator' },
    { name: 'Bullet List', icon: 'List', command: 'insertUnorderedList' },
    { name: 'Numbered List', icon: 'ListOrdered', command: 'insertOrderedList' },
    { name: 'Separator', type: 'separator' },
    { name: 'Link', icon: 'Link', command: 'createLink' },
    { name: 'Image', icon: 'Image', command: 'insertImage' },
    { name: 'Quote', icon: 'Quote', command: 'formatBlock', value: 'blockquote' }
  ];

  const handleToolbarAction = (button) => {
    if (button?.command === 'createLink') {
      const url = prompt('Enter URL:');
      if (url) {
        document.execCommand(button?.command, false, url);
      }
    } else if (button?.command === 'insertImage') {
      onImageInsert();
    } else if (button?.value) {
      document.execCommand(button?.command, false, button?.value);
    } else {
      document.execCommand(button?.command, false, null);
    }
    
    editorRef?.current?.focus();
    updateWordCount();
  };

  const updateWordCount = () => {
    if (editorRef?.current) {
      const text = editorRef?.current?.textContent || '';
      const words = text?.trim()?.split(/\s+/)?.filter(word => word?.length > 0);
      setWordCount(words?.length);
    }
  };

  const handleContentInput = (e) => {
    const newContent = e?.target?.innerHTML;
    onContentChange(language, 'content', newContent);
    updateWordCount();
  };

  const handleTitleChange = (e) => {
    onContentChange(language, 'title', e?.target?.value);
  };

  const handleExcerptChange = (e) => {
    onContentChange(language, 'excerpt', e?.target?.value);
  };

  return (
    <div className="space-y-6">
      {/* Title Input */}
      <div>
        <Input
          label={language === 'ar' ? 'عنوان المقال' : language === 'tr' ? 'Makale Başlığı' : 'Article Title'}
          type="text"
          placeholder={
            language === 'ar' ?'أدخل عنوان المقال...' 
              : language === 'tr' ?'Makale başlığını girin...' :'Enter article title...'
          }
          value={content?.title || ''}
          onChange={handleTitleChange}
          className={isRTL ? 'text-right' : 'text-left'}
          required
        />
      </div>
      {/* Rich Text Editor */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-foreground">
            {language === 'ar' ? 'محتوى المقال' : language === 'tr' ? 'Makale İçeriği' : 'Article Content'}
          </label>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowToolbar(!showToolbar)}
            iconName={showToolbar ? 'EyeOff' : 'Eye'}
            iconPosition="left"
            iconSize={16}
          >
            {showToolbar ? 'Hide Toolbar' : 'Show Toolbar'}
          </Button>
        </div>

        {/* Toolbar */}
        {showToolbar && (
          <div className="flex flex-wrap items-center gap-1 p-3 bg-muted rounded-lg border border-border">
            {toolbarButtons?.map((button, index) => (
              button?.type === 'separator' ? (
                <div key={index} className="w-px h-6 bg-border mx-1" />
              ) : (
                <Button
                  key={button?.name}
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={() => handleToolbarAction(button)}
                  iconName={button?.icon}
                  iconSize={16}
                  title={button?.name}
                />
              )
            ))}
          </div>
        )}

        {/* Editor */}
        <div className="relative">
          <div
            ref={editorRef}
            contentEditable
            className={`
              min-h-96 p-4 bg-background border border-border rounded-lg 
              focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent
              prose prose-sm max-w-none
              ${isRTL ? 'text-right' : 'text-left'}
            `}
            style={{ direction: isRTL ? 'rtl' : 'ltr' }}
            onInput={handleContentInput}
            dangerouslySetInnerHTML={{ __html: content?.content || '' }}
            data-placeholder={
              language === 'ar' ?'ابدأ في كتابة مقالك هنا...' 
                : language === 'tr' ?'Makalenizi yazmaya buradan başlayın...' :'Start writing your article here...'
            }
          />
          
          {/* Word Count */}
          <div className="absolute bottom-2 right-2 text-xs text-muted-foreground bg-background/80 px-2 py-1 rounded">
            {wordCount} {language === 'ar' ? 'كلمة' : language === 'tr' ? 'kelime' : 'words'}
          </div>
        </div>
      </div>
      {/* Excerpt */}
      <div>
        <Input
          label={language === 'ar' ? 'مقتطف المقال' : language === 'tr' ? 'Makale Özeti' : 'Article Excerpt'}
          type="text"
          placeholder={
            language === 'ar' ?'مقتطف قصير للمقال (اختياري)...' 
              : language === 'tr' ?'Makale için kısa özet (isteğe bağlı)...' :'Short excerpt for the article (optional)...'
          }
          value={content?.excerpt || ''}
          onChange={handleExcerptChange}
          className={isRTL ? 'text-right' : 'text-left'}
          description={
            language === 'ar' ?'سيظهر هذا المقتطف في قوائم المقالات' 
              : language === 'tr' ?'Bu özet makale listelerinde görünecek' :'This excerpt will appear in article listings'
          }
        />
        <div className="text-xs text-muted-foreground mt-1">
          {(content?.excerpt || '')?.length}/200 {language === 'ar' ? 'حرف' : language === 'tr' ? 'karakter' : 'characters'}
        </div>
      </div>
    </div>
  );
};

export default ContentEditor;